import React, { useEffect, useState, useRef } from "react";
import { onSnapshot, collection, doc, getDoc, query, where, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useTenant } from "../context/TenantContext";
import StyledTable from "./StyledTable";
import ConfirmDialog from "./ConfirmDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";

const AppointmentList = () => {
  const [appointments, setAppointments] = useState([]);
  const [search, setSearch] = useState("");
  const { tenantId, loading } = useTenant();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);

  // 🔹 Cache for patient/doctor lookups to avoid duplicate Firestore reads
  const patientCache = useRef({});
  const doctorCache = useRef({});

  useEffect(() => {
    if (loading || !tenantId) return;

    const q = query(collection(db, "appointments"), where("tenantId", "==", tenantId));

    const unsubscribe = onSnapshot(q, async (snap) => {
      const updatedAppointments = await Promise.all(
        snap.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const { patientId, doctorId, datetime, prescriptionId } = data;

          if (!patientId || !doctorId) return null;

          // 🔹 Fetch patient (cache if possible)
          let patientName = "Unknown";
          let patientPhone = "Unknown";
          if (patientCache.current[patientId]) {
            ({ name: patientName, phone: patientPhone } = patientCache.current[patientId]);
          } else {
            const patientDoc = await getDoc(doc(db, "patients", patientId));
            if (patientDoc.exists()) {
              const pdata = patientDoc.data();
              patientCache.current[patientId] = pdata;
              patientName = pdata.name;
              patientPhone = pdata.phone;
            }
          }

          // 🔹 Fetch doctor (cache if possible)
          let doctorName = "Unknown";
          if (doctorCache.current[doctorId]) {
            doctorName = doctorCache.current[doctorId].name;
          } else {
            const doctorDoc = await getDoc(doc(db, "doctors", doctorId));
            if (doctorDoc.exists()) {
              const ddata = doctorDoc.data();
              doctorCache.current[doctorId] = ddata;
              doctorName = ddata.name;
            }
          }

          return {
            id: docSnap.id,
            datetime: datetime
              ? datetime.toDate().toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              }) +
              ", " +
              datetime.toDate().toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              })
              : "",
            patientName,
            patientPhone,
            doctorName,
            prescriptionId: prescriptionId || null,
          };
        })
      );

      setAppointments(updatedAppointments.filter((a) => a !== null));
    });

    return () => unsubscribe();
  }, [tenantId, loading]);

  const handleDeleteClick = (appointment) => {
    setAppointmentToDelete(appointment);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteDoc(doc(db, "appointments", appointmentToDelete.id));
      toast.success("Appointment deleted");
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete");
    } finally {
      setConfirmOpen(false);
      setAppointmentToDelete(null);
    }
  };

  const filtered = appointments.filter(
    (a) =>
      a.patientName?.toLowerCase().includes(search.toLowerCase()) ||
      a.patientPhone?.toLowerCase().includes(search.toLowerCase()) ||
      a.doctorName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="patient-list-container">
      <div className="search-container">
        <input
          type="text"
          placeholder="Search by patient or doctor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <StyledTable
        headers={["Date & Time", "Patient", "Phone", "Doctor", "Actions"]}
        data={filtered}
        renderRow={(a) => (
          <>
            <div>{a.datetime}</div>
            <div>{a.patientName}</div>
            <div>{a.patientPhone}</div>
            <div>{a.doctorName}</div>
            <div className="delete-btn" onClick={() => handleDeleteClick(a)}>
              <FontAwesomeIcon icon="trash" className="unauth-trash-icon" />
            </div>
          </>
        )}
      />
      <ConfirmDialog
        open={confirmOpen}
        title="Confirm delete?"
        message={`Are you sure you want to delete this appointment?`}
        onConfirm={confirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

export default AppointmentList;
